import { apiClient } from '../axiosConfig';
import { Enquiry } from './types';

export interface StartJourneyRequest {
  journeyId: string;
  version: string;
  turnstileToken?: string;
  clientTimeZoneId?: string;
}

export interface StartJourneyResponse {
  enquiryId: string;
}

export interface ExecuteStepRequest {
  journeyId: string;
  enquiryId: string;
  version: string;
  currentStepId: string;
  currentStepData: Record<string, string | string[]>;
  clientTimeZoneId?: string;
}

export interface ExecuteStepResponse {
  success: boolean;
  errorMessage?: string;
  nextStepId?: string;
  isComplete: boolean;
}

export interface VerifyCodeRequest {
  enquiryId: string;
  currentStepId: string;
  code: string;
}

export interface VerifyCodeResponse {
  valid: boolean;
  status?: string;
}

export interface ResendCodeRequest {
  enquiryId: string;
  currentStepId: string;
}

export interface ResendCodeResponse {
  success: boolean;
}

export interface SendCodeRequest {
  enquiryId: string;
  currentStepId: string;
  email: string;
}

export interface SendCodeResponse {
  success: boolean;
}

export interface CreatePortalInviteResponse {
  inviteId: string;
  redirectUrl: string;
}

export const enquiryApi = {
  start: async (request: StartJourneyRequest): Promise<StartJourneyResponse> => {
    const response = await apiClient.post<StartJourneyResponse>(
      `/api/journeys/${request.journeyId}/start/${request.version}`,
      { turnstileToken: request.turnstileToken, clientTimeZoneId: request.clientTimeZoneId }
    );
    return response.data;
  },

  get: async (enquiryId: string): Promise<Enquiry> => {
    const response = await apiClient.get<Enquiry>(
      `/api/journeys/enquiries/${enquiryId}`
    );
    return response.data;
  },

  executeNextStep: async (request: ExecuteStepRequest): Promise<ExecuteStepResponse> => {
    // Transform the request to match the server's model
    const serverRequest = {
      journeyId: request.journeyId,
      enquiryId: request.enquiryId,
      version: request.version,
      currentStepId: request.currentStepId,
      currentStepData: request.currentStepData,
      clientTimeZoneId: request.clientTimeZoneId,
    };
    
    const response = await apiClient.post<ExecuteStepResponse>(
      '/api/pipeline/next',
      serverRequest
    );
    return response.data;
  },

  verifyCode: async (request: VerifyCodeRequest): Promise<VerifyCodeResponse> => {
    const response = await apiClient.post<VerifyCodeResponse>(
      '/api/vonage/verify-code',
      {
        enquiryId: request.enquiryId,
        currentStepId: request.currentStepId,
        code: request.code,
      }
    );
    return response.data;
  },

  createPortalInvite: async (enquiryId: string): Promise<CreatePortalInviteResponse> => {
    const response = await apiClient.post<CreatePortalInviteResponse>("/api/journeys/portal-invites", {
      enquiryId,
    });
    return response.data;
  },

  resendCode: async (request: ResendCodeRequest): Promise<ResendCodeResponse> => {
    const response = await apiClient.post<ResendCodeResponse>(
      '/api/vonage/resend-code',
      {
        enquiryId: request.enquiryId,
        currentStepId: request.currentStepId,
      }
    );
    return response.data;
  },

  sendCode: async (request: SendCodeRequest): Promise<SendCodeResponse> => {
    const response = await apiClient.post<SendCodeResponse>(
      '/api/vonage/send-code',
      {
        enquiryId: request.enquiryId,
        currentStepId: request.currentStepId,
        email: request.email,
      }
    );
    return response.data;
  },
};